import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CommsProvider } from './context/CommsContext';
import { LoginForm } from './components/shared/LoginForm';
import { ProtectedRoute } from './components/shared/ProtectedRoute';
import { LoadingSpinner } from './components/ui';
import HomeAssetLeadPage from './pages/HomeAssetLeadPage_v2';
import CarAssetLeadPage from './pages/CarAssetLeadPage';
import CarSalesDemo from './pages/CarSalesDemo';
import CarSalesIntro from './pages/CarSalesIntro';

// Lazy load page components

const AiScalingImpact = lazy(() => import('./pages/AiScalingImpact'));


// Loading fallback component
const PageLoadingSpinner: React.FC = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="text-center">
      <LoadingSpinner size="lg" />
      <p className="mt-4 text-gray-600">Loading...</p>
    </div>
  </div>
);


// Main app content
const AppContent: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();

  console.log('AppContent - isAuthenticated:', isAuthenticated, 'isLoading:', isLoading);

  if (isLoading) {
    return <PageLoadingSpinner />;
  }

  return (
    <Suspense fallback={<PageLoadingSpinner />}>
      <Routes>
        {/* Public, password-protected lead/demo pages (auth handled inside each page) */}
        <Route path="/car-asset-lead" element={<CarAssetLeadPage />} />
        <Route path="/car-sales-demo" element={<CarSalesDemo />} />
        <Route path="/car-sales-intro" element={<CarSalesIntro />} />

        {/* Everything else falls back to the authenticated app or the lead page */}
        <Route path="*" element={<AuthenticatedRoutes isAuthenticated={isAuthenticated} />} />
      </Routes>
    </Suspense>
  );
};

// Authenticated app routes (or the public home lead page when not signed in)
const AuthenticatedRoutes: React.FC<{ isAuthenticated: boolean }> = ({ isAuthenticated }) => {
  if (!isAuthenticated) {
    return <HomeAssetLeadPage />;
  }

  console.log('User is authenticated, rendering main app');
  return (
    <CommsProvider>
      <Routes>
        <Route path="/" element={<Navigate to="/projects" replace />} />
        <Route path="*" element={<Navigate to="/projects" replace />} />
      </Routes>
    </CommsProvider>
  );
};

// Main App component
const App: React.FC = () => {
  return (
    <Router>
      <AuthProvider>
        <div className="min-h-screen bg-gray-50">
          <AppContent />
        </div>
      </AuthProvider>
    </Router>
  );
};

export default App;
